import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './MiniNewsWidget.css';
import { NewsHeadlineType, NewsStoryType } from '../../types/news.type';
import NewsStory from '../../Components/News/NewsStory';
import Pagination from '../../Components/Common/Pagination/Pagination';
import { paginate, getMaxPageNum } from '../../utils/pagination';
import Modal from '../../Components/Common/Modal/Modal';
import { getHeadlines, getNewsStory } from '../../api/news.api';
import moment from 'moment';

import useElementDimension from '../../hooks/useElementDimension';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';

import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { defaultPageNum, errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import useHeightBasedItems from '../../hooks/useHeightBasedItems';
import CustomTooltip from '../../Components/Common/CustomTooltip';

export interface MiniNewsWidgetProps extends BaseWidgetProps {
  token?: string;
  rics?: string;
  symbol?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const isAllSymbolsInvalid = (symbol = '', invalidSymbols: string[]) => {
  const symbols = symbol
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  return symbols.length > 0 && symbols.every((s) => invalidSymbols.includes(s));
};

const MiniNewsWidget = (props: MiniNewsWidgetProps) => {
  const { onHeightChange } = props;
  const theme = props.theme ?? ThemeType.Direct;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'newsWidgets');

  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.MiniNews, onHeightChange);
  const { width, isSmallScreen: isMobile } = useContainerWidth(widgetRef);
  const headlinesRef = useRef<HTMLDivElement | null>(null);
  // Headlines unmount while the mobile story is open, so keep their scroll
  // position to restore on close
  const headlinesScrollTopRef = useRef(0);

  const [isInitial, setIsInitial] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState<NewsHeadlineType[]>([]);
  const [nid, setNid] = useState<any>(undefined);
  const [newsStory, setNewsStory] = useState<NewsStoryType>();
  const [narrowNewsStory, setNarrowNewsStory] =
    useState<NewsStoryType | null>();
  const [isLast, setIsLast] = useState(false);
  const [page, setPage] = useState(1);
  const { height } = useElementDimension(headlinesRef);

  const MAXIMUM_RESULTS_PER_PAGE = 5;
  const [maxPageNum, setMaxPageNum] = useState(defaultPageNum);

  const reqNewsNumber = isMobile ? 20 : 100;

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const mobileDisplayedItems = data;
  const canLoadMore = !isLast;

  const {
    displayedItems,
    itemsPerPage: PER_PAGE,
    totalPages,
    isMeasuring,
  } = useHeightBasedItems(headlinesRef, data, page, [data, height, isMobile]);

  // Symbol actually sent to the API; cleared when every provided symbol is invalid
  const effectiveSymbolRef = useRef(props.symbol);
  // Bumped on every initial load so late next-page responses for the previous
  // symbol/rics are dropped
  const loadGenerationRef = useRef(0);
  const isPrefetchingRef = useRef(false);

  useEffect(() => {
    effectiveSymbolRef.current = props.symbol;
    setIsInitial(true);
  }, [props.rics, props.symbol]);

  useEffect(() => {
    if (!isInitial || width === 0) return;
    let cancelled = false;
    loadGenerationRef.current++;
    const run = async () => {
      try {
        setIsLoading(true);
        setIsEmptyData(false);
        let res = await getHeadlines({
          token: props.token || '',
          limit: reqNewsNumber,
          rics: props.rics,
          symbol: props.symbol,
        });
        if (cancelled) return;
        const invalidSymbols = res?.invalidSymbols ?? [];
        if (invalidSymbols.length > 0) {
          props.onError?.({
            message: `Invalid symbols: ${invalidSymbols.join(';')}`,
            type: 'INVALID_SYMBOL',
          });
          // All symbols invalid -> load default state (same as empty symbol)
          if (isAllSymbolsInvalid(props.symbol, invalidSymbols)) {
            effectiveSymbolRef.current = '';
            res = await getHeadlines({
              token: props.token || '',
              limit: reqNewsNumber,
              rics: props.rics,
              symbol: '',
            });
            if (cancelled) return;
          }
        }
        const headlines = res?.newsHeadlines ?? [];
        if (headlines[0]) {
          setNid(headlines[0].newsId);
        }
        setData(headlines);
        setIsLast(headlines.length !== reqNewsNumber);
        setPage(1);
        setIsEmptyData(headlines.length === 0);
        if (headlines.length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'MiniNewsWidget',
            data: null,
          });
        }
      } catch (err) {
        console.log(err);
        props.onError?.({ message: err as string, type: 'API_ERROR' });
        setIsEmptyData(true);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsInitial(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isInitial, width]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!nid) return;
      try {
        const res = await getNewsStory(props.token || '', nid);
        if (cancelled) return;
        if (res.story) {
          setNewsStory(res.story);
          setNarrowNewsStory(res.story);
        }
      } catch (err) {
        console.log(err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [nid, isMobile]);

  const fetchNextHeadlines = async () => {
    const last = data[data.length - 1];
    const res = await getHeadlines({
      token: props.token || '',
      limit: reqNewsNumber,
      rics: props.rics,
      symbol: effectiveSymbolRef.current,
      newsId: last.newsId,
      newsRT: last.rt,
    });
    return res?.newsHeadlines ?? [];
  };

  // Not cancelled on dep changes: the appended data itself re-triggers this
  // effect, so cancelling would drop the response and fire the same request
  // again. Only one prefetch runs at a time; stale loads are dropped by generation.
  useEffect(() => {
    if (
      isMobile ||
      isMeasuring || // totalPages is a transient 1 while measuring
      isPrefetchingRef.current ||
      isLast ||
      data.length === 0 ||
      totalPages - page > MAXIMUM_RESULTS_PER_PAGE
    )
      return;
    const generation = loadGenerationRef.current;
    isPrefetchingRef.current = true;
    const run = async () => {
      try {
        const headlines = await fetchNextHeadlines();
        if (generation !== loadGenerationRef.current) return;
        setData((prev) => [...prev, ...headlines]);
        if (headlines.length !== reqNewsNumber) setIsLast(true);
      } catch (err) {
        if (generation !== loadGenerationRef.current) return;
        console.log(err);
        props.onError?.({ message: err as string, type: 'API_ERROR' });
      } finally {
        isPrefetchingRef.current = false;
      }
    };
    run();
  }, [page, PER_PAGE, totalPages, isLast, isMobile, isMeasuring]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = async () => {
    if (isLast || isLoadingMore || data.length === 0) return;
    const generation = loadGenerationRef.current;
    setIsLoadingMore(true);
    onLoadingChangeRef.current?.(true);
    try {
      const headlines = await fetchNextHeadlines();
      if (generation !== loadGenerationRef.current) return;
      setData((prev) => [...prev, ...headlines]);
      if (headlines.length !== reqNewsNumber) setIsLast(true);
    } catch (err) {
      console.log(err);
      props.onError?.({ message: err as string, type: 'API_ERROR' });
    } finally {
      setIsLoadingMore(false);
      onLoadingChangeRef.current?.(false);
    }
  };

  useEffect(() => {
    if (width > 0) {
      setMaxPageNum(getMaxPageNum(width, true, false, false));
    }
  }, [width]);

  const handlePageChange = React.useCallback(async (page: number) => {
    try {
    } catch (e) {
      setPage(1);
    }
  }, []);

  const handleHeadlineClick = async (newsId: string) => {
    if (nid && nid !== newsId) {
      if (isMobile) setNarrowNewsStory(null);
    }
    setNid(newsId);
    if (isMobile) {
      headlinesScrollTopRef.current = headlinesRef.current?.scrollTop ?? 0;
      setOpenModal(true);
    }
  };

  const handleModalClose = React.useCallback(() => {
    setOpenModal(false);
  }, []);

  // Layout effect so the list never paints at the top before jumping back
  useLayoutEffect(() => {
    if (!isMobile || openModal || !headlinesRef.current) return;
    headlinesRef.current.scrollTop = headlinesScrollTopRef.current;
  }, [openModal, isMobile]);

  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;
  useEffect(() => {
    if (!isMobile || !widgetRef.current) return;

    const sendHeightMessage = () => {
      requestAnimationFrame(() => {
        if (!widgetRef.current) return;
        const height = widgetRef.current.scrollHeight;
        const message: WidgetHeightMessage = {
          type: WidgetMessageType.Height,
          source: WidgetNameSource.MiniNews,
          height,
        };

        onHeightChangeRef.current?.(message);

        if ((window as any).ReactNativeWebView?.postMessage) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify(message),
          );
        }
      });
    };

    sendHeightMessage();
  }, [openModal, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderHeadline = (item: NewsHeadlineType) => {
    // BE omits `co` (or returns "undefined" entries) when symbols are invalid
    const cos = Array.isArray(item.co)
      ? item.co.filter((c) => c && c !== 'undefined')
      : [];
    return (
      <div key={item.newsId} className="wrapper">
        <div className="wrapper-header">
          {cos.length > 0 ? (
            <CustomTooltip title={cos.join('\n')} placement="auto-end">
              <div className="ric">
                <span className="ric-name">{cos[0]}</span>
                {cos.length >= 2 && (
                  <span className="extra-data">{`+ ${cos.length - 1} more`}</span>
                )}
              </div>
            </CustomTooltip>
          ) : (
            <div className="ric" />
          )}
          <span className="dateTime">{`${moment(item.date).format('DD MMM')} ${item.time}`}</span>
        </div>
        <CustomTooltip title={item.headline} placement="bottom-start">
          <span
            className="headline"
            onClick={() => handleHeadlineClick(item.newsId)}
          >
            {item.headline}
          </span>
        </CustomTooltip>
      </div>
    );
  };

  return (
    <Layout theme={theme}>
      <div
        ref={widgetRef}
        className={`miniNews${isMobile ? ' mobile-view' : ''}`}
      >
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="wrapper">
            <>
              {!openModal && (
                <div className="headlines-container">
                  <div
                    ref={headlinesRef}
                    className={`headlines${isMobile ? ' headlines-mobile' : ''}`}
                  >
                    {(isMobile ? mobileDisplayedItems : displayedItems).map(
                      renderHeadline,
                    )}
                    {isMobile && canLoadMore && (
                      <button
                        type="button"
                        className="load-more-btn"
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                      >
                        Load more
                      </button>
                    )}
                  </div>
                  {!isMobile && (
                    <Pagination
                      pagination={paginate(page, 1, totalPages, maxPageNum)}
                      onPageClick={(targetPage: number) => {
                        setPage(targetPage);
                        handlePageChange(targetPage);
                      }}
                      haveLastPage={isLast}
                    />
                  )}
                </div>
              )}
              <div className="story">
                {newsStory ? (
                  <NewsStory
                    headline={newsStory.headline}
                    date={newsStory.date}
                    time={newsStory.time}
                    story={newsStory.body}
                  />
                ) : null}
              </div>
            </>
            {isMobile && openModal && (
              <div className="modal-story">
                {narrowNewsStory ? (
                  <NewsStory
                    headline={narrowNewsStory.headline}
                    date={narrowNewsStory.date}
                    time={narrowNewsStory.time}
                    story={narrowNewsStory.body}
                    withCloseButton={true}
                    onClose={handleModalClose}
                  />
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MiniNewsWidget;
